import React, { useState, useRef } from "react";
import { Grid, TextField, Paper } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import { LoadingSpinner } from "../../../utils/LoadingSpinner";
import { S3_CONFIG } from "../config/UPMSConfig";
import { useS3Operations } from "../hooks";
import { COLORS } from "../styles/theme";
import {
  StyledHeaderBox,
  ResponseDisplay,
  StyledActionButton,
} from "../components";

interface IProps {
  data?: any;
}

const RetrieveRxFromS3Section: React.FC<IProps> = () => {
  const [fileName, setFileName] = useState("");
  const responseRef = useRef<HTMLDivElement>(null);

  // Use the S3 operations hook
  const {
    isRetrieving,
    retrievedData,
    error: s3Error,
    retrieveFromS3,
  } = useS3Operations();

  const scrollToResponse = (): void => {
    // Small delay to ensure the response element is rendered
    setTimeout(() => {
      if (responseRef.current) {
        responseRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  };

  const handleFileNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(event.target.value);
  };

  const handleRetrieve = async () => {
    if (!fileName.trim()) {
      return;
    }

    try {
      await retrieveFromS3(fileName);
      scrollToResponse();
    } catch (err) {
      // Error is already handled by the hook
    }
  };

  return (
    <Grid container direction="column" justifyContent="center" spacing={3}>
      <StyledHeaderBox
        title="Retrieve Rx from S3"
        subtitle={`Retrieval source: ${S3_CONFIG.getFullPath()}`}
        awsAccount="pillpack-erx-beta"
        color="#546e7a"
        infoMessage="Retrieve prescription files from S3 storage. Enter the full file name including extension (e.g., fileName.json)."
      />

      {/* Input Section */}
      <Paper
        elevation={1}
        style={{
          padding: 32,
          borderRadius: 8,
          backgroundColor: COLORS.white,
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label="File Name"
              variant="outlined"
              value={fileName}
              onChange={handleFileNameChange}
              placeholder="e.g., 123456.json"
              fullWidth
              helperText="Enter the full file name including extension (e.g., fileName.json)"
            />
          </Grid>

          {s3Error && (
            <Grid item xs={12}>
              <Alert severity="error">
                <strong>S3 Retrieval Error:</strong> {s3Error}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <StyledActionButton
              label="Retrieve Rx File"
              loadingLabel="Retrieving..."
              onClick={handleRetrieve}
              disabled={!fileName.trim() || !!retrievedData}
              loading={isRetrieving}
              color={COLORS.orange}
              startIcon={<CloudDownloadIcon />}
            />
          </Grid>
        </Grid>
      </Paper>

      {isRetrieving && <LoadingSpinner />}

      {retrievedData && (
        <ResponseDisplay
          title="Retrieved File Content"
          subtitle="File retrieved successfully from S3"
          data={retrievedData}
          color={COLORS.iconGray}
          icon="✓"
          forwardRef={responseRef}
        />
      )}
    </Grid>
  );
};

export default RetrieveRxFromS3Section;
